import { ResponseGeneric } from './generic';

export type NewCasinoCategory = {
  id: string;
  name: string;
  title: string;
  games_count: string;
  gameCount?: string;
  categoryId?: string;
  badge?: string | null;
};

export type NewCasinoProvider = {
  name: string;
  order: string;
  title: string;
  blocked_countries: string[];
  badge: null | { title: string; class: string };
  icon_name?: string;
};

export type TNewCasinoDataGamesByAllGroup = {
  count: string;
  games: NewCasinoGameBackend[];
};

export type TNewCasinoDataByAllGroup = {
  status: string;
  games: Record<string, TNewCasinoDataGamesByAllGroup>;
};

export type TNewCasinoData = {
  status: string;
  count: string;
  total_count: string;
  games: NewCasinoGameBackend[];
};

export type TNewCasinoDataWithCounts = {
  status: string | number;
  count: string;
  total_count: string;
  games: Record<string, { count: string }> | NewCasinoGameBackend[];
};

export enum ENewCasinoFailedDataMessage {
  TimeoutError = 'Timeout Error',
  SomethingWentWrong = 'Something Went Wrong'
}

export type TNewCasinoFailedData = {
  status: -1;
  message: ENewCasinoFailedDataMessage;
};

export type NewCasinoGameBackend = {
  active: string;
  background?: string;
  backgrounds: string[];
  badge?: { title: string; class: string };
  blocked_countries: string[];
  blocked_currencies: string[];
  categories: string[];
  cats: NewCasinoGameCategory[];
  description: string | null;
  extearnal_game_id: string;
  extra_category?: {
    id: string;
    title: string;
    name: string;
  };
  feats: [];
  features: [];
  front_game_id: string;
  game_options: null | string;
  game_skin_id: string;
  has_age_restriction: number;
  icon_1: string;
  icon_2: string;
  icon_3: string;
  icons: string[];
  id: string;
  markets?: string;
  name: string;
  provider: string;
  provider_badge: null;
  provider_title: string;
  ratio: string;
  rtp: null | string;
  server_game_id: string;
  show_as_provider: string;
  status: string;
  themes: [];
  thms: [];
  title: string | null;
  types: NewCasinoGameTypes;
  bet_limits?: Record<string, { min: number; max: number }>;
};

export type NewCasinoGame = Pick<
  NewCasinoGameBackend,
  | 'background'
  | 'badge'
  | 'blocked_countries'
  | 'blocked_currencies'
  | 'categories'
  | 'cats'
  | 'description'
  | 'extearnal_game_id'
  | 'game_options'
  | 'has_age_restriction'
  | 'icon_1'
  | 'icon_2'
  | 'icon_3'
  | 'id'
  | 'name'
  | 'provider'
  | 'provider_title'
  | 'types'
  | 'bet_limits'
>;

type NewCasinoGameTypes = {
  realMode: number;
  funMode?: number;
  viewMode?: number;
};

type NewCasinoGameCategory = {
  id: string;
  title: string;
  type: string;
};

export type NewCasinoGamesConfigs = {
  categoryIds?: string;
  providerIds?: string;
  moduleId: number;
  casinoGamesLimit: number;
  showCategoryName: boolean;
  showLoadMoreBtn: boolean;
  bindWithCategories: boolean;
  bindWithProviders: boolean;
  groupAllByCategories: boolean;
  loadMoreType?: number;
  gamesRows?: number;
  gamesPerRow: number;
  mobileCustomSettings?: boolean;
  mobileShowLoadMoreBtn?: boolean;
  mobileLoadMoreType?: number;
  mobileCasinoGamesLimit?: number;
  mobileGamesRows?: number;
  mobileGamesPerRow?: number;
  mobileShowArrow?: boolean;
  skipQueryKeysCheck?: boolean;
};

export enum ENewCasinoGamesFetchingParamsKey {
  Id = 'id',
  Category = 'category'
}

export type NewCasinoGamesFetchingParams = {
  partner_id: number;
  is_mobile: number;
  lang: string;
  country: string;
  by_key?: ENewCasinoGamesFetchingParamsKey;
  offset?: number;
  limit?: number;
  id?: string;
  external_id?: string[] | string;
  use_webp: number;
  category?: string;
  _fake_category?: string; // this is a fake param to force the React Query to run correctly
  _all_categories?: string; // this is a fake param to force replacing category param in case of all games count fetch
  provider?: string;
  search?: string;
  with_bet_limits?: number;
  jackpot_id?: number;
  player_id?: number;
};

export type DecentralizedCasinoGamesFetchingParams = {
  partnerId: number;
  gameId: string;
};

export type NewCasinoGamesFetchingParamsPartial =
  Partial<NewCasinoGamesFetchingParams>;

export type CasinoLastPlayedGamesResponse = Omit<ResponseGeneric, 'details'> & {
  details: {
    _id: string;
    game_ids: { _id: string }[];
  };
};

export type CasinoSuggestedGamesResponse = {
  ExternalId?: number | null;
  GameIds?: number[];
  PlayerId?: number;
};

export type DecentralizedCasinoGamesData = {
  count: number;
  status?: string;
  games: DecentralizedCasinoGame[];
};
export type DecentralizedCasinoGame = {
  categoryId: string;
  categories: number[];
  externalGameId: number;
  funMode: number;
  icon1: string;
  icon2: string;
  icon3: string;
  isMobile: number;
  name: string;
  providerType: number;
  realMode: number;
};

export type TTournamentGames = {
  items: NewCasinoGame[];
  total_count: string;
};

export type IGetCasinoGamesResponse =
  | TNewCasinoDataByAllGroup
  | TNewCasinoData
  | DecentralizedCasinoGamesData
  | TNewCasinoFailedData;
